import {useTranslationContext} from 'platform/components';

import {suffixTestId, type TestIdProps} from 'shared/web';

type ResultSummaryProps = {
  /** Zero-based page index (matches store `page`). */
  currentPage: number;
  itemsPerPage: number;
  totalCount: number;
  /**
   * Controls which i18n key is used.
   * - `'showing'` (default): "Showing {from}–{to} of {total}" — used in the toolbar.
   * - `'range'`: "{from}–{to} of {total}" — used in the footer, no "Showing" prefix.
   */
  variant?: 'showing' | 'range';
} & TestIdProps;

// Sentinel placeholders interpolated in place of the numbers so the translated
// string can be split into "word" vs "number" segments regardless of each
// locale's word order. They never appear in any real translation, so the split
// is unambiguous.
const FROM_TOKEN = '[[FROM]]';
const TO_TOKEN = '[[TO]]';
const TOTAL_TOKEN = '[[TOTAL]]';
const TOKEN_SPLIT = /(\[\[FROM\]\]|\[\[TO\]\]|\[\[TOTAL\]\])/;

export function ResultSummary(props: ResultSummaryProps) {
  const t = useTranslationContext();

  const from = props.totalCount === 0 ? 0 : props.currentPage * props.itemsPerPage + 1;
  const to = Math.min((props.currentPage + 1) * props.itemsPerPage, props.totalCount);

  const key =
    props.variant === 'range'
      ? 'page.datagrid.labels.resultRangeShort'
      : 'page.datagrid.labels.resultRange';

  // The mock bolds the numbers (.tb__count b → fg-primary, weight 600) while the
  // surrounding words stay secondary.
  const boldByToken: Record<string, number> = {
    [FROM_TOKEN]: from,
    [TO_TOKEN]: to,
    [TOTAL_TOKEN]: props.totalCount,
  };
  const segments = t(key, {
    from: FROM_TOKEN,
    to: TO_TOKEN,
    total: TOTAL_TOKEN,
  }).split(TOKEN_SPLIT);

  return (
    <span
      className="text-[13px] text-[#42526E] tabular-nums"
      data-testid={suffixTestId('resultSummary', props)}
    >
      {segments.map((segment, index) =>
        segment in boldByToken ? (
          // Inline weight: the <b> UA bold (700) wins over the font-semibold
          // utility here; the mock's .tb__count b is 600.
          // eslint-disable-next-line react/no-array-index-key -- Pre-existing violation
          <b key={index} className="text-[#091E42]" style={{fontWeight: 600}}>
            {boldByToken[segment]}
          </b>
        ) : (
          segment
        )
      )}
    </span>
  );
}
